import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TimeSignal } from 'graphql/types';

type ReturnType = {
  selectAll: boolean;
  indeterminate: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
export const useNotesItemHeaderCheckActions = ({
  notes,
  watchId,
  totalCount
}: {
  notes: TimeSignal[];
  watchId: string;
  totalCount: number;
}): ReturnType => {
  const { control, setValue } = useFormContext();
  const noteIds = useMemo(() => {
    const ids = new Set<string>();
    notes.forEach((note) => ids.add(`${note.date}_${note.id}`));
    return Array.from(ids);
  }, [notes]);

  const formValues = useWatch({
    name: [...noteIds, watchId],
    control
  });

  const values = useMemo<Record<string, boolean>>(
    () =>
      [...noteIds, watchId].reduce(
        (acc, item, index) => ({
          ...acc,
          [`${item}`]: formValues[index] ?? false
        }),
        {}
      ),
    [watchId, noteIds, formValues]
  );

  const selectedCount = useMemo(
    () =>
      Object.keys(values).reduce(
        (acc, key) => (key.indexOf('_') >= 0 && values[key] ? acc + 1 : acc),
        0
      ),
    [values]
  );

  const { selectAll, indeterminate } = useMemo(() => {
    return {
      selectAll: totalCount > 0 && selectedCount === totalCount,
      indeterminate: selectedCount > 0 && selectedCount < totalCount
    };
  }, [totalCount, selectedCount]);

  const onChange = useCallback(() => {
    setValue(watchId, selectAll, { shouldDirty: true });
    noteIds.forEach((id) =>
      setValue(id, !(selectAll || indeterminate), { shouldDirty: true })
    );
  }, [noteIds, setValue, watchId, indeterminate, selectAll]);

  return { selectAll, indeterminate, onChange };
};
